import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Descriptions,
  Tag,
  Table,
  Button,
  Space,
  Alert,
  Statistic,
  Timeline,
  Typography,
  Divider,
  message
} from 'antd';
import {
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import advertisementAPI, { Advertisement, AdvertisementLog } from '../../services/advertisementAPI';

const { Text, Title } = Typography;

interface AdvertisementDetailsProps {
  advertisement: Advertisement;
  onClose: () => void;
}

const AdvertisementDetails: React.FC<AdvertisementDetailsProps> = ({
  advertisement,
  onClose
}) => {
  const [logs, setLogs] = useState<AdvertisementLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);

  useEffect(() => {
    if (advertisement.id) {
      loadLogs();
    }
  }, [advertisement.id]);

  const loadLogs = async () => {
    if (!advertisement.id) return;
    
    setLogsLoading(true);
    try {
      const result = await advertisementAPI.getAdvertisementLogs(advertisement.id);
      if (result.success) {
        setLogs(result.data);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Logları yükləmə xətası');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!advertisement.id) return;

    setSendLoading(true);
    try {
      const result = await advertisementAPI.sendAdvertisement(advertisement.id);
      if (result.success) {
        message.success(result.message);
        // Logları yenidən yüklə
        setTimeout(() => {
          loadLogs();
        }, 1000);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Göndərmə xətası');
    } finally {
      setSendLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hazırlanır': return 'blue';
      case 'göndərilir': return 'orange';
      case 'göndərilib': return 'green';
      case 'xəta': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'hazırlanır': return 'Hazırlanır';
      case 'göndərilir': return 'Göndərilir';
      case 'göndərilib': return 'Göndərilib';
      case 'xəta': return 'Xəta';
      default: return status;
    }
  };

  const getLogStatusIcon = (status: string) => {
    switch (status) {
      case 'uğurlu': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'uğursuz': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'gözləyir': return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default: return null;
    }
  };

  const logColumns = [
    {
      title: 'Alıcı',
      dataIndex: 'recipient_name',
      key: 'recipient_name',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Space>
          {getLogStatusIcon(status)}
          <span>{status === 'uğurlu' ? 'Uğurlu' : status === 'uğursuz' ? 'Uğursuz' : 'Gözləyir'}</span>
        </Space>
      ),
    },
    {
      title: 'Göndərilmə tarixi',
      dataIndex: 'sent_at',
      key: 'sent_at',
      render: (date: string) => date ? new Date(date).toLocaleString('az-AZ') : '-',
    },
    {
      title: 'Xəta',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (error: string) => error ? <Text type="danger">{error}</Text> : '-',
    },
  ];

  const successRate = advertisement.total_recipients! > 0 
    ? (advertisement.successful_sends! / advertisement.total_recipients!) * 100 
    : 0;

  return (
    <div>
      {/* Əsas məlumatlar */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <Card title="Reklam Məlumatları" size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Başlıq">
                <Text strong>{advertisement.title}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item label="Növ">
                <Tag color={advertisement.message_type === 'reklam' ? 'purple' : 'cyan'}>
                  {advertisement.message_type === 'reklam' ? 'Reklam' : 'İnformativ'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(advertisement.status!)}>
                  {getStatusText(advertisement.status!)}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="Yaradan">
                {advertisement.created_by_name}
              </Descriptions.Item>
              
              <Descriptions.Item label="Yaradılma tarixi">
                {new Date(advertisement.created_at!).toLocaleString('az-AZ')}
              </Descriptions.Item>
              
              <Descriptions.Item label="Göndərilmə tarixi">
                {advertisement.sent_at ? new Date(advertisement.sent_at).toLocaleString('az-AZ') : '-'}
              </Descriptions.Item>
            </Descriptions>
            
            <Divider />
            
            <div>
              <Text strong>Mesaj məzmunu:</Text>
              <div style={{ 
                marginTop: 8, 
                padding: 12, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 6,
                whiteSpace: 'pre-wrap'
              }}>
                {advertisement.content}
              </div>
            </div>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title="Statistikalar" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Alıcılar"
                  value={advertisement.total_recipients}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="SMS sayı"
                  value={advertisement.total_sms_count}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Uğurlu"
                  value={advertisement.successful_sends}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Uğursuz"
                  value={advertisement.failed_sends}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Statistic
              title="Uğur nisbəti"
              value={successRate}
              suffix="%"
              precision={1}
              valueStyle={{ 
                color: successRate > 80 ? '#52c41a' : successRate > 50 ? '#faad14' : '#ff4d4f' 
              }}
            />
            
            {advertisement.status === 'hazırlanır' && (
              <>
                <Divider />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={sendLoading}
                  block
                  disabled={!advertisement.total_recipients || advertisement.total_recipients === 0}
                >
                  Reklamı Göndər
                </Button>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* SMS qaydaları */}
      {advertisement.sms_rules_info && (
        <Card title="SMS Hesablaması" size="small" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Alert
                message="SMS Qaydaları"
                description={
                  <div style={{ fontSize: '12px' }}>
                    {advertisement.sms_rules_info.rules.map((rule, index) => (
                      <div key={index}>• {rule}</div>
                    ))}
                  </div>
                }
                type="info"
                showIcon
              />
            </Col>
            <Col span={12}>
              <Alert
                message="Hazırki Hesablama"
                description={
                  <div>
                    <p><strong>Simvol sayı:</strong> {advertisement.sms_rules_info.character_count}</p>
                    <p><strong>SMS/alıcı:</strong> {advertisement.sms_rules_info.current_sms_count}</p>
                    <p><strong>Ümumi SMS:</strong> {advertisement.sms_rules_info.total_sms_needed}</p>
                  </div>
                }
                type="success"
                showIcon
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Göndərmə logları */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Göndərmə Logları</span>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadLogs}
              loading={logsLoading}
              size="small"
            >
              Yenilə
            </Button>
          </div>
        }
        size="small"
      >
        {logs.length > 0 ? (
          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="id"
            loading={logsLoading}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {advertisement.status === 'hazırlanır' 
              ? 'Reklam hələ göndərilməyib'
              : 'Göndərmə logu tapılmadı'
            }
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdvertisementDetails;